import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Row-level privacy disclosure for staged chat attachments.
 *
 * The #193 redaction note is GLOBAL, STATIC information — identical
 * for every attachment, a property of the pipeline (images go
 * verbatim to the Anthropic API and persist in this game's local SDK
 * session for resume), not of any one image. It's an engineer-added
 * safety affordance, not a legal requirement. So it surfaces ONCE per
 * attachment row behind a single warm (i), rather than repeating on
 * every thumbnail or shouting as an always-visible banner.
 */

/** #193 redaction note — verbatim meaning, tightened wording. */
export const ATTACHMENT_REDACTION_NOTE =
  "Images are sent verbatim to Anthropic and kept in this game's local session for resume. " +
  'Crop or redact secrets, tokens, and personal info before sending.';

export function AttachmentRedactionInfo(): React.JSX.Element {
  const noteId = React.useId();

  return (
    <span
      // `self-end` aligns the (i) to the BOTTOM of the taller image
      // thumbnails (the row is `items-center`), so it reads as a quiet
      // trailing footnote rather than floating mid-height.
      className="group/info relative inline-flex self-end focus-within:z-20 hover:z-20"
      data-testid="chat-attachment-info"
    >
      <button
        type="button"
        aria-label="How attached images are handled"
        aria-describedby={noteId}
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full',
          'border border-amber-300/40 bg-amber-300/10 font-serif text-[11px] italic',
          'text-amber-300/90 transition-colors hover:border-amber-300/70 hover:text-amber-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50',
        )}
      >
        i
      </button>
      <span id={noteId} className="sr-only">
        {ATTACHMENT_REDACTION_NOTE}
      </span>
      {/* Visual gap is `pb-2` PADDING on this positioning wrapper, not
          margin, so the cursor stays inside the hover group while
          moving from the (i) up to the note — no dead gap that drops
          `group-hover/info` and hides it (cursor[bot] #1865). */}
      <span
        className={cn(
          'absolute bottom-full left-0 hidden pb-2',
          'group-focus-within/info:block group-hover/info:block',
        )}
      >
        <span
          role="tooltip"
          data-testid="chat-attachment-redaction-warning"
          className={cn(
            'block w-max max-w-[300px]',
            'rounded-[var(--radius-lg)] border border-amber-300/20 bg-[#18151f] p-2.5',
            'text-[11px] leading-snug text-amber-200/80 shadow-xl',
          )}
        >
          {ATTACHMENT_REDACTION_NOTE}
        </span>
      </span>
    </span>
  );
}
